import React, { ReactNode, PureComponent } from 'react'
import ReactDOM from 'react-dom'
import classNames from 'classnames'
import { Fade } from 'Components//animation'

import Portal from 'Components/portal'

export interface IToastProps {
  type: string
  title?: ReactNode
  description?: ReactNode
  className?: string
  duration?: number
  onClose?: () => void
  close?: () => void
  visible: boolean
  getContainer?: () => HTMLElement
  isModal: boolean
}

export type TToastFunc = (
  config: Partial<IToastProps>,
  duration?: number,
  onClose?: () => void
) => {
  hide: () => void
}

export default class Toast extends PureComponent<IToastProps> {
  closeTimer?: number

  static success: TToastFunc
  static error: TToastFunc

  static defaultProps: Partial<IToastProps> = {
    duration: 3000,
    type: 'info',
    visible: false,
  }

  componentDidMount() {
    this.startCloseTimer()
  }

  componentDidUpdate(prevProps: IToastProps) {
    const { duration } = this.props
    if (duration !== prevProps.duration) {
      this.startCloseTimer()
    }
  }

  startCloseTimer() {
    const { duration } = this.props
    if (duration) {
      this.closeTimer && clearTimeout(this.closeTimer)
      this.closeTimer = window.setTimeout(() => {
        this.handleClose()
      }, duration)
    }
  }

  handleClose() {
    const { onClose, close } = this.props
    onClose && onClose()
    close && close()
  }

  getContainer = (): HTMLElement => {
    const { getContainer } = this.props
    if (getContainer) {
      return getContainer()
    }
    const container = document.createElement('div')
    document.body.appendChild(container)
    return container
  }

  getComponent() {
    const { className, title, description, type, visible, isModal = true, duration } = this.props
    return (
      <>
        <Fade in={visible} timeout={duration || 200} unmountOnExit>
          <div className="toast-wrap">
            <div className={classNames('toast', className)}>
              <div className={`toast-${type} icon-auto-icon-unselect'`} />
              <div className="toast-content">
                <div className="toast-title">{title}</div>
                <div className="toast-desc">{description}</div>
              </div>
            </div>
          </div>
        </Fade>
        <style jsx>
          {`
            .show {
              opacity: 1;
            }
            .toast-wrap {
              position: fixed;
              top: 0;
              bottom: 0;
              left: 0;
              right: 0;
              z-index: 101000;
              width: 100%;
              height: 100%;
              background-color: ${isModal ? 'rgba(0, 0, 0, 0.6)' : 'rgba(0, 0, 0, 0)'};
              pointer-events: none;
            }
            .toast {
              position: absolute;
              top: 50%;
              left: 50%;
              transform: translate3d(-50%, -50%, 0);
              background: #fff;
              height: 68px;
              border-radius: 4px;
              box-shadow: 0 2px 10px 0 rgba(0, 0, 0, 0.06);
              padding: 20px 24px;
              display: flex;
              align-items: center;
              justify-content: space-between;
            }

            .toast-success {
              font-size: 62px;
              color: #54c844;
              float: left;
              height: 23px;
              width: 23px;
              background: url(${require('./static/success.png')}) no-repeat 0 0 /
                cover;
            }
            .toast-error {
              font-size: 62px;
              color: #54c844;
              float: left;
              height: 23px;
              width: 23px;
              background: url(${require('./static/error.png')}) no-repeat 0 0 /
                cover;
            }
            .toast-success::before {
              content: '';
            }
            .toast-content {
              margin-left: 4px;
              float: right;
              text-align: center;
            }

            .toast-title {
              font-stretch: normal;
              font-style: normal;
              line-height: 1.4;
              letter-spacing: normal;
              color: #1f2129;
              font-size: 14px;
              font-weight: 500;
            }

            .toast-desc {
              font-size: 20px;
              color: #666;
              line-height: 1;
            }
          `}
        </style>
      </>
    )
  }

  render() {
    return (
      <Portal getContainer={this.getContainer}>{this.getComponent()}</Portal>
    )
  }
}

export function openToast(config: Partial<IToastProps>) {
  const div = document.createElement('div')
  document.body.appendChild(div)

  function render(props: IToastProps) {
    ReactDOM.render(<Toast {...props} />, div)
  }

  function close() {
    render({
      ...(config as IToastProps),
      getContainer: () => div,
      // @ts-ignore
      onClose: destory.bind(this),
      visible: false,
    })
  }

  function destory() {
    const unmountResult = ReactDOM.unmountComponentAtNode(div)
    if (unmountResult && div.parentNode) {
      div.parentNode.removeChild(div)
    }
  }

  render({
    ...(config as IToastProps),
    // @ts-ignore
    onClose: destory.bind(this),
    getContainer: () => div,
    close,
    visible: true,
  })

  return {
    hide: close,
  }
}

Toast.success = (config, duration, onClose) => {
  return openToast({
    ...config,
    duration,
    onClose,
    type: 'success',
  })
}

Toast.error = (config, duration, onClose) => {
  return openToast({
    ...config,
    duration,
    onClose,
    type: 'error',
  })
}

